import { apiFetch, apiFetchBlob, ApiError } from './client';

// Wire types for the UI sign-off module. The snapshot shapes mirror
// @mount-insights/ui-snapshot's schema (vendored in mi-portal-api/src/vendor/ui-snapshot/types.ts);
// keep them in step when the schema version changes.

export type DefSource = 'overlay' | 'core' | 'standard' | 'org' | 'undefined';
export type Access = 'granted' | 'not_granted' | 'unknown';

export interface FieldDef {
  label: string;
  type: string;
  referenceTo?: string[];
  formula?: boolean;
  help?: string;
  picklist?: string[];
  required?: boolean;
  catalogued?: boolean;
  relabeledFrom?: string;
  source: DefSource;
}

export interface VisibilityRule { criteria: { left: string; operator: string; right: string | null }[]; booleanFilter?: string }
/** Where an item sits in its page file (extractor 0.3+): what the layout editor writes back by. */
export interface NodeProvenance { identifier?: string; facets?: Record<string, string> }
export interface FieldNode { kind: 'field'; field: string; behavior: string | null; visibility?: VisibilityRule; provenance?: NodeProvenance }
export interface ComponentNode { kind: 'component'; name: string; props: Record<string, string>; slots: Record<string, PageNode[]>; visibility?: VisibilityRule; provenance?: NodeProvenance }
export type PageNode = FieldNode | ComponentNode;

export interface Page { label: string; template: string | null; regions: { name: string; items: PageNode[] }[]; source: DefSource; provenance?: { path: string; templateProps?: Record<string, string> } }
export interface LayoutItem { field: string; behavior: string | null }
export interface LayoutSection { label: string; style: string | null; columns: LayoutItem[][]; provenance?: { index: number } }
export interface Layout {
  sections: LayoutSection[];
  /** Related list columns by the name a page gives the list; custom ones qualified (CampaignMember.Attendance__c). Extractor 0.5. */
  relatedColumns?: Record<string, string[]>;
  source: DefSource;
  provenance?: { path: string };
}

export interface Unresolved { code: string; message: string; ref?: string }

export interface View {
  key: string;
  personaId: string;
  recordType: string;
  page: string | null;
  pageSource: 'app_record_type_profile' | 'app_default' | 'org_default' | null;
  app: string | null;
  layout: string | null;
  highlights: { compactLayout: string | null; system: boolean; fields: string[] };
  access: Record<string, Access>;
  fields: string[];
  contentHash: string;
  unresolved: Unresolved[];
}

export interface ObjectSnapshot {
  api: string;
  label: string;
  recordTypes: Record<string, { label: string; active: boolean; compactLayout: string | null; picklists: Record<string, string[]>; source: DefSource }>;
  fields: Record<string, FieldDef>;
  pages: Record<string, Page>;
  layouts: Record<string, Layout>;
  compactLayouts: Record<string, { label: string; fields: string[]; source: DefSource }>;
  components: Record<string, ComponentMeta>;
  relatedLists: Record<string, string>;
  /** Label and type of the related lists' custom columns, by qualified token. Extractor 0.5. */
  relatedFields?: Record<string, { label: string; type: string; referenceTo?: string[] }>;
  views: View[];
}

/** An LWC's source as the snapshot carries it; see ComponentBundle in the vendored library. */
export interface ComponentBundle {
  html: string;
  css?: string;
  text: Record<string, string>;
  columns: Record<string, string[]>;
  children: Record<string, string>;
  properties: Record<string, { label?: string; default?: string }>;
}
export interface ComponentMeta { label: string; description?: string; source: DefSource; bundle?: ComponentBundle }
/** A component the layout editor can place (extractor 0.3+). */
export interface CatalogEntry extends ComponentMeta { objects?: string[]; standard?: boolean }

export interface AppTab { name: string; label: string; object?: string }
export interface AppDef { label: string; navType: string | null; headerColor: string | null; tabs: AppTab[]; hasUtilityBar: boolean; source: DefSource }

export interface Persona { id: string; label: string; profile: string; permissionSets: string[]; apps?: string[] }
export interface Finding { severity: 'blocker' | 'check' | 'note'; code: string; object: string; message: string; detail?: string }

export interface SignoffRow {
  id: string;
  objectApi: string;
  viewKey: string;
  kind: 'signed' | 'revoked';
  revokesId: string | null;
  contentHash: string;
  commitSha: string;
  snapshotId: string;
  extractorVersion: string;
  signerName: string | null;
  signerEmail: string;
  signerRole: string;
  createdAt: string;
}

export type ViewState = 'blocked' | 'ready' | 'signed' | 'changed';
export interface ViewStatus { state: ViewState; signoff: SignoffRow | null; reason: 'renderer_updated' | null }

export interface ModuleInfo {
  configured: boolean;
  repo?: string;
  baselineBranch?: string;
  readableBranches?: string[];
  /** The branch holding a copy of the client's org, shown read-only as "In the org". */
  orgBranch?: string | null;
  /** The environment whose org captures are signed against, e.g. cvdev. Null: none. */
  captureEnv?: string | null;
  objects?: string[];
  canReadRepos?: boolean;
  isStaff?: boolean;
  signer?: { roleLabel: string } | null;
  statement?: { text: string; version: number } | null;
  questionsEnabled?: boolean;
}

export interface SnapshotResponse {
  ref: string;
  stale: boolean;
  /** A fixed version (a SHA or a stored snapshot), not a branch: it will not move. */
  pinned?: boolean;
  snapshotId: string;
  commit: string;
  commitDate: string | null;
  origin: 'github' | 'upload';
  extractorVersion: string;
  repo: string;
  core: { repo: string | null; ref: string | null; namespace: string | null };
  personas: Persona[];
  /** Null for snapshots made before extractor 0.2. */
  apps?: Record<string, AppDef> | null;
  hiddenTabs?: Record<string, string[]> | null;
  findings: Finding[];
  /** The org capture this is, when it is one; for a preview, the capture it is laid over. */
  capture?: Capture | null;
  /** Set for a preview (a branch laid over a capture): the capture's commit. */
  base?: { repo: string | null; commit: string | null } | null;
  object: ObjectSnapshot;
  statuses: Record<string, ViewStatus>;
}

/** An org capture's CAPTURE.json, as the capture workflow wrote it. */
export interface Capture {
  schema: 1;
  org: { id: string; environment: string };
  capturedAt: string;
  capturedBy: string | null;
  trigger: string;
  objects: string[];
  installedPackages: { namespace: string | null; name: string; version: string }[];
  lastDeployed: { branch: string | null; commit: string; pr: number | null; at: string | null } | null;
}

/** One org capture, newest first: the org's metadata at one time, tagged in the client repo. */
export interface CaptureVersion {
  tag: string;
  commit: string;
  capturedAt: string | null;
  orgId: string | null;
  environment: string;
  trigger: string | null;
  capturedBy: string | null;
  coreVersion: string | null;
  lastDeployed: Capture['lastDeployed'];
  /** Objects reviewed here that the capture didn't retrieve. */
  missingObjects: string[];
  /** Set when the tag has no readable CAPTURE.json: it can't be opened. */
  problem: string | null;
  snapshotId: string | null;
  /** Against the capture before: true when a page differs, false when none do, null when unknown. */
  pagesChanged: boolean | null;
  latest: boolean;
}

export interface CaptureList {
  environment: string | null;
  captures: CaptureVersion[];
  /** The commit the environment's deployed/<env> marker names. */
  deployed: { sha: string; date: string | null } | null;
  /** True when the environment has deployed since the latest capture. */
  deployedSinceCapture: boolean | null;
}

export interface ViewDiff {
  key: string;
  status: 'added' | 'removed' | 'changed' | 'same';
  fieldsAdded: string[];
  fieldsRemoved: string[];
  sectionsAdded: string[];
  sectionsRemoved: string[];
  tabsAdded: string[];
  tabsRemoved: string[];
  pageChanged: boolean;
  layoutChanged: boolean;
}

/**
 * One version of a branch: a distinct set of rendered pages, from the commit that brought them to
 * the newest commit that still draws them. `number` counts from the oldest; the list is newest first.
 */
export interface Version {
  /** Null for a commit that was seen but never rendered. */
  number: number | null;
  /**
   * first: the first pages the portal saw; change: a commit changed the pages; renderer: a newer
   * renderer drew them and whether the commit changed them too is unknown; setup: the review setup
   * (objects, personas, paths left out) changed; unrendered: never drawn.
   */
  kind: 'first' | 'change' | 'renderer' | 'setup' | 'unrendered';
  /** The commit that brought these pages. */
  commit: string;
  /** For a merge commit, when it merged. Null for an upload that gave no date. */
  commitDate: string | null;
  /** The title of the commit that changed the pages; null unless `kind` is change. */
  title: string | null;
  seenAt: string;
  /** The newest commit with these pages: the one a version opens at. */
  latestCommit: string;
  commits: string[];
  /** Drawn by a different renderer from the version before, so some differences may be its own. */
  rendererChanged: boolean;
  /** False when the commit was seen but never rendered, so it can't be opened. */
  available: boolean;
  current: boolean;
}

export interface DiffResponse { base: { ref: string; commit: string; object: ObjectSnapshot }; head: { ref: string; commit: string }; views: ViewDiff[] }

export type DecisionStatus = 'on_layout' | 'admin_only' | 'hidden_phase_2' | 'not_on_layout' | 'remove' | 'check' | 'withdrawn';
export interface Decision {
  id: string;
  objectApi: string;
  fieldApi: string | null;
  proposedLabel: string | null;
  status: DecisionStatus;
  placement: string | null;
  note: string | null;
  source: string | null;
  authorName: string | null;
  createdAt: string;
}

export interface Question {
  id: string;
  anchorObject: string;
  anchorRef: string;
  anchorCommit: string | null;
  question: string;
  answer: string | null;
  status: string;
  createdAt: string;
  notes: { id: string; body: string; authorName: string | null; createdAt: string }[];
}

const base = (slug: string) => `/clients/${encodeURIComponent(slug)}/modules/ui-signoff`;
const q = (o: Record<string, string>) => new URLSearchParams(o).toString();

export const getInfo = (slug: string, token: string | null) => apiFetch<ModuleInfo>(`${base(slug)}/`, token);
/** `ref` is an allowed branch or tag, or a full commit SHA already rendered; `snapshotId` opens one stored snapshot. */
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * A commit's first view is read from the repo while the request waits; past about 20 seconds the
 * API answers 202 `{ rendering: true }` and keeps reading. Ask again until it's ready.
 */
export async function untilRendered<T>(call: () => Promise<T | { rendering: true }>, pollMs = 3_000, tries = 60, onDrawing?: () => void): Promise<T> {
  for (let i = 0; i < tries; i++) {
    const r = await call();
    if (!(r as { rendering?: boolean } | null)?.rendering) return r as T;
    onDrawing?.();
    await sleep(pollMs);
  }
  throw new ApiError(504, 'Still reading the repo after several minutes. Try again in a moment.');
}

/** `onDrawing` is called each time the API answers that it is still drawing the pages. */
export const getSnapshot = (slug: string, object: string, ref: string, token: string | null, snapshotId?: string | null, onDrawing?: () => void) =>
  untilRendered(() => apiFetch<SnapshotResponse | { rendering: true }>(`${base(slug)}/snapshot?${q(snapshotId ? { object, snapshotId } : { object, ref })}`, token, { timeoutMs: 120_000 }), 3_000, 60, onDrawing);
export const getCaptures = (slug: string, token: string | null) => apiFetch<CaptureList>(`${base(slug)}/captures`, token);

// ---------- pages and sign-off rounds ----------

export type PageState = 'not_reviewed' | 'signed_this_round' | 'signed_unchanged' | 'changed_since_signed';
export interface PageStatus {
  state: PageState;
  signoff: { id: string; roundId: string; signedAt: string; signerName: string | null } | null;
  movedViews: string[];
}
/** One page of a capture: a record page with its layout, and every view it serves. */
export interface CapturePage {
  objectApi: string;
  objectLabel: string;
  pageKey: string;
  label: string;
  flexipage: string | null;
  layout: string | null;
  views: { key: string; personaId: string; persona: string; recordType: string; recordTypeLabel: string; recordTypeVisible: boolean | null; contentHash: string; signable: boolean }[];
  fingerprint: string;
  status: PageStatus;
}
export interface RoundSummary {
  id: string;
  number: number;
  title: string;
  captureTag: string;
  captureCommit: string;
  snapshotId: string;
  openedByName: string | null;
  openedAt: string;
  closedByName: string | null;
  closedAt: string | null;
  closeNote: string | null;
  pageCount?: number;
}
export interface RoundDetail extends RoundSummary {
  pages: (CapturePage & { addedAt: string; addedByName: string | null })[];
  counts: { pages: number; signed: number };
}

/** Every page of a capture; the capture may still be drawing on its first view. */
export const getPages = (slug: string, tag: string, token: string | null, onDrawing?: () => void) =>
  untilRendered(() => apiFetch<{ capture: string; snapshotId: string; commit: string; pages: CapturePage[] } | { rendering: true }>(`${base(slug)}/pages?${q({ capture: tag })}`, token, { timeoutMs: 120_000 }), 3_000, 60, onDrawing);
export const listRounds = (slug: string, token: string | null) => apiFetch<RoundSummary[]>(`${base(slug)}/rounds`, token);
export const getRound = (slug: string, id: string, token: string | null) => apiFetch<RoundDetail>(`${base(slug)}/rounds/${encodeURIComponent(id)}`, token);
export const openRound = (slug: string, body: { captureTag: string; title: string; pages: string[] }, token: string | null) =>
  untilRendered(() => apiFetch<RoundSummary | { rendering: true }>(`${base(slug)}/rounds`, token, { method: 'POST', body: JSON.stringify(body), timeoutMs: 120_000 }));
export const addRoundPages = (slug: string, id: string, pages: string[], token: string | null) =>
  apiFetch<{ added: number }>(`${base(slug)}/rounds/${encodeURIComponent(id)}/pages`, token, { method: 'POST', body: JSON.stringify({ pages }) });
/** A page sign-off as stored: the page, its capture, the agreement and the receipt's hash. */
export interface PageSignoff {
  id: string;
  kind: 'signed' | 'revoked';
  revokesId: string | null;
  roundId: string;
  objectApi: string;
  pageKey: string;
  pageLabel: string;
  views: { key: string; persona: string; recordType: string; contentHash: string }[];
  pageFingerprint: string;
  captureTag: string;
  captureCommit: string;
  orgId: string | null;
  capturedAt: string | null;
  coreVersion: string | null;
  deployedCommit: string | null;
  deployedPr: number | null;
  buildCommit: string | null;
  buildPr: number | null;
  signerName: string | null;
  signerEmail: string;
  signerRole: string | null;
  statementText: string | null;
  receiptSha256: string;
  createdAt: string;
}
/** Signs one page of a round. `pictures` holds one SVG per view key, drawn in this browser. */
export const signPage = (slug: string, roundId: string, pageKey: string, pictures: Record<string, string>, token: string | null) =>
  apiFetch<PageSignoff>(`${base(slug)}/rounds/${encodeURIComponent(roundId)}/sign`, token, { method: 'POST', body: JSON.stringify({ pageKey, pictures }), timeoutMs: 120_000 });
export const revokePageSignoff = (slug: string, id: string, token: string | null) =>
  apiFetch<PageSignoff>(`${base(slug)}/page-signoffs/${encodeURIComponent(id)}/revoke`, token, { method: 'POST', timeoutMs: 60_000 });
export const listPageSignoffs = (slug: string, token: string | null) => apiFetch<PageSignoff[]>(`${base(slug)}/page-signoffs`, token);
export interface RegisterRow extends PageSignoff {
  round: { number: number; title: string } | null;
  /** For a sign-off that was revoked: the revocation's id. */
  revokedBy: string | null;
  /** Against the latest capture: true, false, or null when that can't be told (another renderer, not rendered yet). */
  stillMatches: boolean | null;
  /** The viewer's own sign-off, which they may revoke. */
  mine: boolean;
}
export const getRegister = (slug: string, token: string | null) =>
  apiFetch<{ latestCapture: { tag: string; capturedAt: string | null; rendered: boolean } | null; rows: RegisterRow[] }>(`${base(slug)}/register`, token);
export const getReceipt = (slug: string, id: string, token: string | null) => apiFetch<PageSignoff>(`${base(slug)}/receipts/${encodeURIComponent(id)}`, token);
export const getReceiptPdf = (slug: string, id: string, token: string | null) => apiFetchBlob(`${base(slug)}/receipts/${encodeURIComponent(id)}/pdf`, token, { timeoutMs: 60_000 });
export const closeRound = (slug: string, id: string, note: string, token: string | null) =>
  apiFetch<RoundSummary>(`${base(slug)}/rounds/${encodeURIComponent(id)}/close`, token, { method: 'POST', body: JSON.stringify({ note }) });
export const getVersions = (slug: string, ref: string, token: string | null) =>
  apiFetch<{ ref: string; versions: Version[] }>(`${base(slug)}/versions?${q({ ref })}`, token);
/** Each side is an allowed branch, or a full SHA already rendered (a version). */
export const getDiff = (slug: string, object: string, baseRef: string, head: string, token: string | null, onDrawing?: () => void) =>
  untilRendered(() => apiFetch<DiffResponse | { rendering: true }>(`${base(slug)}/diff?${q({ object, base: baseRef, head })}`, token, { timeoutMs: 120_000 }), 3_000, 60, onDrawing);
export const getDecisions = (slug: string, object: string, token: string | null) =>
  apiFetch<{ current: Decision[]; history: Decision[] }>(`${base(slug)}/decisions?${q({ object })}`, token);
export const postDecision = (slug: string, body: Omit<Decision, 'id' | 'createdAt' | 'authorName' | 'objectApi'> & { object: string }, token: string | null) =>
  apiFetch<Decision>(`${base(slug)}/decisions`, token, { method: 'POST', body: JSON.stringify(body) });
export const getQuestions = (slug: string, object: string, token: string | null) => apiFetch<Question[]>(`${base(slug)}/questions?${q({ object })}`, token);
export const postQuestion = (slug: string, body: { object: string; anchorRef: string; commit: string | null; question: string }, token: string | null) =>
  apiFetch<Question>(`${base(slug)}/questions`, token, { method: 'POST', body: JSON.stringify(body) });
export const postNote = (slug: string, questionId: string, body: string, token: string | null) =>
  apiFetch<{ ok: true }>(`${base(slug)}/questions/${questionId}/notes`, token, { method: 'POST', body: JSON.stringify({ body }) });
export const signView = (slug: string, body: { snapshotId: string; object: string; viewKey: string }, token: string | null) =>
  apiFetch<SignoffRow>(`${base(slug)}/signoffs`, token, { method: 'POST', body: JSON.stringify(body) });
export const revokeSignoff = (slug: string, id: string, token: string | null) =>
  apiFetch<SignoffRow>(`${base(slug)}/signoffs/${id}/revoke`, token, { method: 'POST' });

/** The API's stable error code from an ApiError, e.g. "not_a_signer". */
export function errorCode(e: unknown): string | null {
  if (!(e instanceof ApiError)) return null;
  const m = /\{.*\}$/.exec(e.message);
  try { return m ? (JSON.parse(m[0]).error as string) : null; } catch { return null; }
}
export function errorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    const m = /\{.*\}$/.exec(e.message);
    try { if (m) { const b = JSON.parse(m[0]); return b.message ?? b.error; } } catch { /* fall through */ }
  }
  return e instanceof Error ? e.message : String(e);
}

// ---------- admin ----------

/** Apps and personas kept in the portal; the extractor's UiSignoffConfig. */
export interface PersonasConfig {
  apps: string[];
  personas: Persona[];
  core?: { namespace: string; dependency?: string };
}

export interface AdminConfig {
  repo: string;
  baselineBranch: string;
  readableBranches: string[];
  coreRepo: string | null;
  coreRef: string | null;
  coreRoot: string | null;
  enabledObjects: string[];
  qaTrackerId: string | null;
  statementText: string | null;
  /** Null: read config/ui-signoff.json from the repo. */
  personasConfig: PersonasConfig | null;
  /** Path globs in the repo to leave out, e.g. retired metadata still in a package directory. */
  excludePaths: string[];
  /** The branch holding a copy of the org, e.g. mirror/acme--cvdev. Null: none. */
  orgBranch: string | null;
  /** The environment whose org captures are signed against (tags capture/<env>/...). Null: none. */
  captureEnv: string | null;
  statementVersion?: number;
}
export interface AdminState {
  config: (AdminConfig & { statementVersion: number }) | null;
  signers: { userId: string; email: string; name: string | null; roleLabel: string }[];
  clientUsers: { userId: string; email: string; name: string | null }[];
  trackers: { id: string; name: string }[];
  canReadRepos: boolean;
}
const adminBase = (clientId: string) => `/admin/clients/${clientId}/ui-signoff`;
export const getAdmin = (clientId: string, token: string | null) => apiFetch<AdminState>(adminBase(clientId), token);
export const saveAdmin = (clientId: string, cfg: AdminConfig, token: string | null) =>
  apiFetch<AdminConfig>(adminBase(clientId), token, { method: 'PUT', body: JSON.stringify(cfg) });
export const setSigner = (clientId: string, userId: string, roleLabel: string | null, token: string | null) =>
  apiFetch<{ ok: true }>(`${adminBase(clientId)}/signers/${userId}`, token, { method: 'PUT', body: JSON.stringify({ roleLabel }) });
export const uploadSnapshot = (clientId: string, ref: string, snapshot: unknown, token: string | null) =>
  apiFetch<{ id: string; commit: string; ref: string }>(`${adminBase(clientId)}/snapshots`, token, { method: 'POST', body: JSON.stringify({ ref, snapshot }), timeoutMs: 60_000 });
export interface ImportSummary { dryRun: boolean; decisions: number; questions: number; skippedDecisions: number; skippedQuestions: number }
export const importDictionary = (
  clientId: string,
  body: { object: string; source: string; dryRun: boolean; decisions: unknown[]; questions: unknown[] },
  token: string | null,
) => apiFetch<ImportSummary>(`${adminBase(clientId)}/import`, token, { method: 'POST', body: JSON.stringify(body), timeoutMs: 60_000 });

// ---------- layout proposals ----------

/** A layout editor's edits: the pages and layouts it changed, whole, by name. */
export interface ProposalChanges { pages?: Record<string, Page>; layouts?: Record<string, Layout> }
export type ProposalStatus = 'draft' | 'shared' | 'built' | 'withdrawn';
export interface Proposal {
  id: string;
  objectApi: string;
  viewKey: string | null;
  title: string;
  baseSnapshotId: string;
  baseCommit: string;
  status: ProposalStatus;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
  revision: number;
  revisedByName: string | null;
  revisedAt: string;
}
export interface ProposalDetail extends Proposal {
  changes: ProposalChanges;
  revisions: { revision: number; note: string | null; authorName: string | null; createdAt: string }[];
}
export interface ProposalFiles { files: { path: string; content: string }[]; skipped: { name: string; reason: string }[]; changes: string }

const json = (body: unknown) => ({ method: 'POST', body: JSON.stringify(body) });
export const getCatalog = (slug: string, object: string, snapshotId: string, token: string | null) =>
  apiFetch<{ catalog: Record<string, CatalogEntry>; available: boolean }>(`${base(slug)}/catalog?${q({ object, snapshotId })}`, token);
export const listProposals = (slug: string, object: string, token: string | null) =>
  apiFetch<Proposal[]>(`${base(slug)}/proposals?${q({ object })}`, token);
export const getProposal = (slug: string, id: string, token: string | null) =>
  apiFetch<ProposalDetail>(`${base(slug)}/proposals/${encodeURIComponent(id)}`, token);
export const createProposal = (slug: string, body: { object: string; viewKey: string | null; title: string; baseSnapshotId: string; changes: ProposalChanges; note?: string | null }, token: string | null) =>
  apiFetch<Proposal>(`${base(slug)}/proposals`, token, json(body));
export const reviseProposal = (slug: string, id: string, changes: ProposalChanges, token: string | null, note: string | null = null) =>
  apiFetch<Proposal>(`${base(slug)}/proposals/${encodeURIComponent(id)}/revisions`, token, json({ changes, note }));
export const updateProposal = (slug: string, id: string, patch: { status?: ProposalStatus; title?: string }, token: string | null) =>
  apiFetch<Proposal>(`${base(slug)}/proposals/${encodeURIComponent(id)}`, token, { method: 'PATCH', body: JSON.stringify(patch) });
export const getProposalFiles = (slug: string, id: string, token: string | null) =>
  apiFetch<ProposalFiles>(`${base(slug)}/proposals/${encodeURIComponent(id)}/files`, token, { timeoutMs: 120_000 });

export interface RevisionEntry { revision: number; note: string | null; authorName: string | null; createdAt: string; summary: string[] }
/** Every revision, newest first, with what each changed from the one before; with `revision`, that one's changes too. */
export const getProposalHistory = (slug: string, id: string, token: string | null, revision?: number) =>
  apiFetch<{ revisions: RevisionEntry[]; changes?: ProposalChanges }>(`${base(slug)}/proposals/${encodeURIComponent(id)}/history${revision ? `?revision=${revision}` : ''}`, token);
